/**
 * TanStack Query hooks for the Resource Plan API (M2).
 *
 * Every mutation invalidates by `resourcePlanKeys.all` and owns its own toast — no
 * optimistic cache writes and no `onSuccess` callback props, per .claude/rules/frontend.md.
 *
 * The one thing to know about these: most plan mutations RETURN the whole re-shaped plan,
 * so the grid gets fresh totals, fresh margin and a fresh floor badge from the same round
 * trip that saved the edit. That is what makes RP-7.3 ("the number on screen is never
 * stale") true without a second fetch.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";
import type { PlanLineShareInput } from "@/lib/plan-line-share-edits";
import { type MarginWarningPlan, marginDropWarning } from "@/lib/plan-margin-warning";
import type { PlanStaffing } from "@/lib/plan-staffing";
import { planStatusLabel } from "@/lib/plan-status-labels";
import {
  accountKeys,
  dashboardHeatmapKeys,
  planApprovalKeys,
  planExportKeys,
  planRejectionKeys,
  planVersionKeys,
  resourceDirectoryKeys,
  resourcePlanKeys,
  solutionDashboardKeys,
  solutionDetailKeys,
  staffingRequestKeys,
} from "@/lib/query";
import type { PlanChangePage } from "@/schemas/plan-changes";
import type {
  PlanGuidanceLoadView,
  PlanLineCandidatesView,
  PlanLineFitView,
  PlanOwnerOptionView,
  PlanResourcingStatus,
  ResourcePickerPageView,
  ResourcePlanDetailView,
  ResourcePlanSummaryView,
  SharedPlanView,
  SuggestedResourceReason,
} from "@/schemas/resource-plans";
import { planGuidanceLoadViewSchema } from "@/schemas/resource-plans";
import type { ImportSolutionEstimateResult } from "@/schemas/solution-estimate-import";

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

// Exported for src/hooks/use-estimated-hours-lines.ts — same fetch plumbing, no reason to
// duplicate it for one more resource whose mutations all hang off this same plan detail.
export async function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  return readJsonOrThrow<T>(
    await fetch(url, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

// ── Reads ─────────────────────────────────────────────────────────────────────

export interface PlanListFilters {
  solutionId?: string;
  clientAccountId?: string;
  status?: string;
}

export function useResourcePlans(filters: PlanListFilters = {}, enabled = true) {
  return useQuery({
    queryKey: resourcePlanKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.solutionId) params.set("solutionId", filters.solutionId);
      if (filters.clientAccountId) params.set("clientAccountId", filters.clientAccountId);
      if (filters.status) params.set("status", filters.status);
      const query = params.toString();
      const { plans } = await send<{ plans: ResourcePlanSummaryView[] }>(
        `/api/plans${query ? `?${query}` : ""}`,
        "GET",
      );
      return plans;
    },
    enabled,
  });
}

export function useResourcePlan(planId: string | null) {
  return useQuery({
    queryKey: resourcePlanKeys.detail(planId ?? ""),
    queryFn: () => send<ResourcePlanDetailView>(`/api/plans/${planId}`, "GET"),
    enabled: planId !== null,
  });
}

/**
 * The read-only Plan Overview a plan is shared as. Bill-side only — no cost, no margin,
 * at any grain (see `SharedPlanView` in src/schemas/resource-plans.ts).
 */
export function useSharedPlan(planId: string | null) {
  return useQuery({
    queryKey: resourcePlanKeys.shared(planId ?? ""),
    queryFn: () => send<SharedPlanView>(`/api/plans/${planId}/shared`, "GET"),
    enabled: planId !== null,
  });
}

/** RP-25: what the plan's Changes tab can be narrowed to. */
export interface PlanChangeFilters {
  role?: string;
  planLineId?: string;
  commercial?: boolean;
  from?: string;
  to?: string;
}

/** The first page has no cursor. A function so the param is typed `string | null`. */
function firstChangesPage(): string | null {
  return null;
}

/**
 * The plan's Changes tab, newest first, a page at a time ("Show older changes"). Every
 * plan mutation invalidates `resourcePlanKeys.all`, which this key sits under, so a save
 * shows up here without a manual refresh.
 */
export function usePlanChanges(planId: string, filters: PlanChangeFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: resourcePlanKeys.changes(planId, filters),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "25" });
      if (filters.role) params.set("role", filters.role);
      if (filters.planLineId) params.set("planLineId", filters.planLineId);
      if (filters.commercial) params.set("commercial", "true");
      // The date inputs are the viewer's own days, so send that day's bounds as instants.
      if (filters.from) params.set("from", new Date(`${filters.from}T00:00:00`).toISOString());
      if (filters.to) params.set("to", new Date(`${filters.to}T23:59:59.999`).toISOString());
      if (pageParam !== null) params.set("cursor", pageParam);
      return send<PlanChangePage>(`/api/plans/${planId}/changes?${params}`, "GET");
    },
    initialPageParam: firstChangesPage(),
    getNextPageParam: (last: PlanChangePage) => (last.hasNext ? last.nextCursor : null),
    enabled,
  });
}

/** The person who saved a change gives — or edits, or clears — its reason. */
export function useSetPlanChangeReason(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { eventId: string; reason: string | null }) =>
      send<{ id: string; reason: string | null }>(
        `/api/plan-changes/${input.eventId}/reason`,
        "PUT",
        { reason: input.reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: resourcePlanKeys.changesFor(planId) });
      toast.success("Reason saved");
    },
    onError: (err: Error) => toast.error(err.message ?? "Could not save the reason"),
  });
}

/** RP-32.2 — a Plan Line's Candidates, fetched when the shortlist opens. */
export function usePlanLineCandidates(lineId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.candidates(lineId ?? ""),
    queryFn: () => send<PlanLineCandidatesView>(`/api/plan-lines/${lineId}/candidates`, "GET"),
    enabled: lineId !== null && enabled,
  });
}

/**
 * Candidate writes invalidate everything under `resourcePlanKeys.all` — the shortlist, the
 * plan (its per-line counts) and the picker (its "also on N plans" mentions).
 */
function useInvalidateCandidates() {
  const invalidatePlans = useInvalidatePlans();
  return (_lineId: string) => invalidatePlans();
}

export function useAddPlanLineCandidate() {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: ({ lineId, resourceId }: { lineId: string; resourceId: string }) =>
      send<PlanLineCandidatesView>(`/api/plan-lines/${lineId}/candidates`, "POST", { resourceId }),
    onSuccess: (_view, input) => {
      invalidate(input.lineId);
      toast.success("Candidate added — books no capacity");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the Candidate"),
  });
}

export function useRemovePlanLineCandidate() {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: ({ candidateId }: { lineId: string; candidateId: string }) =>
      send<PlanLineCandidatesView>(`/api/plan-line-candidates/${candidateId}`, "DELETE"),
    onSuccess: (_view, input) => {
      invalidate(input.lineId);
      toast.success("Candidate removed");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the Candidate"),
  });
}

export function usePromotePlanLineCandidate() {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: ({ candidateId }: { lineId: string; candidateId: string }) =>
      send<ResourcePlanDetailView>(`/api/plan-line-candidates/${candidateId}/promote`, "POST"),
    onSuccess: (_plan, input) => {
      invalidate(input.lineId);
      toast.success("Now the Suggested Resource — the rest of the shortlist was cleared");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to promote the Candidate"),
  });
}

/**
 * RP-18.2 / RP-33 — the Resource picker for one Plan Line, cursor-paged ("Load more").
 *
 * The line is the question: the server resolves its Discipline, rating range, Position and
 * active months from `lineId`, filters to Resources holding that Discipline (unless
 * `showAll`), and ranks by free points over the line's months. Rating, availability and the
 * Fit Score arrive only for an actor the server judges may see them.
 */
export function usePlanResourcePicker(
  planId: string | null,
  search: string,
  enabled: boolean,
  lineId?: string,
  showAll = false,
) {
  return useInfiniteQuery({
    queryKey: resourcePlanKeys.picker(planId ?? "", search, lineId ?? "", showAll),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (lineId !== undefined && lineId !== "") params.set("lineId", lineId);
      if (showAll) params.set("showAll", "true");
      if (pageParam !== null) params.set("cursor", pageParam);
      const query = params.toString();
      return send<ResourcePickerPageView>(
        `/api/plans/${planId}/resource-picker${query ? `?${query}` : ""}`,
        "GET",
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: planId !== null && enabled,
    staleTime: 30_000,
  });
}

/**
 * The plan grid's guidance-load overlay — NOS guidance + capacity hours per month, for the
 * Resources suggested on this plan's SAVED lines, narrowed server-side to the
 * ones the actor may view. `suggestedResourceIds` only feeds the query key (see
 * `resourcePlanKeys.guidanceLoad`) so the overlay refetches the moment the saved
 * set changes; the endpoint itself takes no params beyond the plan id.
 */
export function usePlanGuidanceLoad(planId: string, suggestedResourceIds: readonly string[]) {
  return useQuery({
    queryKey: resourcePlanKeys.guidanceLoad(planId, suggestedResourceIds),
    queryFn: async (): Promise<PlanGuidanceLoadView> => {
      const raw = await send<unknown>(`/api/plans/${planId}/guidance-load`, "GET");
      return planGuidanceLoadViewSchema.parse(raw);
    },
  });
}

/**
 * The grid Who column's Fit Scores — one per Plan Line with a suggested Resource, for an
 * actor the server lets see them (empty for everyone else). `lineResources` only feeds the
 * query key (`lineId:resourceId` pairs), so a new pick refetches; the endpoint takes none.
 */
export function usePlanLineFits(planId: string, lineResources: readonly string[]) {
  return useQuery({
    queryKey: resourcePlanKeys.lineFits(planId, lineResources),
    queryFn: async () => {
      const { fits } = await send<{ fits: PlanLineFitView[] }>(
        `/api/plans/${planId}/line-fits`,
        "GET",
      );
      return fits;
    },
    // Keep the chips on screen while a new pick refetches, rather than blanking every row.
    placeholderData: keepPreviousData,
    enabled: lineResources.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Candidates for the plan Owner/Author picker (`PlanOwnerDialog`). `enabled` is the
 * dialog's own open state, per frontend.md — a modal fetches fresh data while it is open,
 * never pre-fetched or passed down as mutable props.
 */
export function usePlanOwnerOptions(enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.ownerOptions(),
    queryFn: async () => {
      const { users } = await send<{ users: PlanOwnerOptionView[] }>(
        "/api/plan-owner-options",
        "GET",
      );
      return users;
    },
    enabled,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Invalidate every plan read. Broad on purpose — a plan edit moves totals everywhere.
 * Exported for src/hooks/use-estimated-hours-lines.ts — a line write is a plan write.
 */
export function useInvalidatePlans() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: resourcePlanKeys.all });
    // The Solutions Dashboard shows Plan Status, and the Account page shows its plans.
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
    // Staffing Requests lists the plans still being staffed, and their open roles.
    qc.invalidateQueries({ queryKey: staffingRequestKeys.openRoles() });
  };
}

export interface CreatePlanInput {
  solutionId: string;
  name: string;
  kind?: string;
  pricingModel: string;
  budgetCadence: string;
  startMonth: string;
  endMonth?: string | null;
  rateMode?: string;
  /** Price off the Account's Rate Card ahead of the standard one. Omit for true. */
  useAccountRateCard?: boolean;
  /** RP-4.4. Omit and it defaults from the cadence. */
  entryMode?: string;
  engagementStandardTierId?: string | null;
  /** RP-2.1: compose the Planned Pod from this Shape in the same transaction (optional). */
  podShapeId?: string;
  estimatedHours?: string | null;
  contractedFee?: string | null;
  contractedFeeBasis?: string | null;
  contractedFeeReference?: string | null;
  nymblDealId?: string | null;
  contingencyPercent?: string | null;
  discountPercent?: string | null;
  discountAmount?: string | null;
  discountReason?: string | null;
  commercialNotes?: string | null;
}

export function useCreateResourcePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: CreatePlanInput) =>
      send<ResourcePlanDetailView>("/api/plans", "POST", input),
    onSuccess: (plan) => {
      invalidate();
      toast.success(`Created "${plan.name}" — ${plan.months.length} month columns`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to create the plan"),
  });
}

/**
 * Joins names the way a sentence would: "A", "A and B", "A, B and C".
 *
 * Exported (minimal, test-only change) so `src/hooks/__tests__/use-resource-plans.test.ts`
 * can cover the phrasing directly, without rendering the toast it feeds.
 */
export function joinNames(names: readonly string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

export function useImportSolutionEstimate() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: { planId: string; projectId: string }) =>
      send<ImportSolutionEstimateResult>(
        `/api/plans/${input.planId}/solution-estimate-import`,
        "POST",
        { projectId: input.projectId },
      ),
    onSuccess: (result) => {
      invalidate();
      const { planLinesCreated, collisions, unmappedRoles, unpriced } = result;

      // Primary: what actually landed on the grid. Counts THIS import's new Plan Lines,
      // never `plan.lines.length` (every line on the plan, including ones this import
      // never touched) — see the api-engineer report on the M4 terminology fix. "role" is
      // this codebase's reserved term for Pod Position / User Role; the pricing dimension
      // here is Labor Category.
      const n = planLinesCreated.length;
      toast.success(
        n === 0
          ? "Imported the Solution estimate, but nothing new was added."
          : `Imported the Solution estimate — ${n} Labor Categor${n === 1 ? "y" : "ies"} added.`,
      );

      // Secondary: cells the import refused to overwrite. Reported so hand-entered hours
      // never silently lose to an import — never folded into the primary toast.
      if (collisions.length > 0) {
        toast.warning(
          `${collisions.length} month${collisions.length === 1 ? "" : "s"} already had hours entered and ${collisions.length === 1 ? "was" : "were"} left untouched.`,
        );
      }

      // Secondary: anything the import couldn't place at all — an unmapped estimator role,
      // or a Labor Category with no resolvable rate. Both are reported here so nothing
      // drops out of view; only the unmapped case has a self-service fix.
      if (unmappedRoles.length > 0 || unpriced.length > 0) {
        const parts: string[] = [];
        if (unmappedRoles.length > 0) {
          const names = joinNames(unmappedRoles.map((r) => r.name));
          parts.push(
            unmappedRoles.length === 1
              ? `${names} isn't mapped to a Labor Category yet — map it in Settings → Labor Categories, then re-run the import.`
              : `${names} aren't mapped to a Labor Category yet — map them in Settings → Labor Categories, then re-run the import.`,
          );
        }
        if (unpriced.length > 0) {
          const names = joinNames(unpriced.map((u) => u.laborCategoryName));
          parts.push(
            unpriced.length === 1
              ? `${names} wasn't priced — no rate is configured for it.`
              : `${names} weren't priced — no rate is configured for them.`,
          );
        }
        toast.warning(parts.join(" "));
      }
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to import the Solution estimate"),
  });
}

/**
 * `estimatedHours` is on `CreatePlanInput` only — once a plan exists its estimate is a
 * server-maintained rollup of its Estimated Hours Lines (`use-estimated-hours-lines.ts`),
 * not a field this PATCH may set. Omitted here rather than reusing
 * `Partial<CreatePlanInput>`, which would silently re-offer it.
 */
export type UpdatePlanInput = Partial<Omit<CreatePlanInput, "estimatedHours">>;

export function useUpdateResourcePlan(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (patch: UpdatePlanInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}`, "PATCH", patch),
    onSuccess: () => {
      invalidate();
      toast.success("Plan updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the plan"),
  });
}

export function useDeleteResourcePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (planId: string) => send<{ ok: true }>(`/api/plans/${planId}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Plan deleted");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to delete the plan"),
  });
}

export interface TransitionInput {
  to: string;
  wonDate?: string | null;
  sowReference?: string | null;
  lostReason?: string | null;
  /**
   * RP-17.1's "the planner's note", carried on a Propose (M4).
   *
   * On the transition rather than on a second call because the planner does not know
   * whether approval is needed — the gate decides that from cost they are not shown.
   */
  approvalNote?: string | null;
  /**
   * Why a status was SET rather than transitioned — the override path only.
   *
   * Optional server-side too: the guardrail for this control is "same as authoring", so an
   * override is offered a justification rather than blocked on one.
   */
  statusChangeReason?: string | null;
}

/** An RM/ADMIN rejects a plan: sets its Resourcing status to Returned, with a required reason. */
export function useTransitionResourcePlan(planId: string) {
  const invalidate = useInvalidatePlans();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransitionInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/transition`, "POST", input),
    onSuccess: (plan) => {
      invalidate();
      // M4 (RP-8.1, RP-17.1): a transition may also freeze a version or raise an Approval
      // Request, and neither lives under `resourcePlanKeys`. Without these, the Versions
      // tab keeps a stale count and the Approval panel does not show the request the
      // planner has just created.
      qc.invalidateQueries({ queryKey: planVersionKeys.all });
      qc.invalidateQueries({ queryKey: planApprovalKeys.all });
      qc.invalidateQueries({ queryKey: planExportKeys.all });
      // Reaching Won can give the Account its first Rate Card from this plan's custom
      // rates (src/api/account-rate-card-seed.ts), and the Account page reads that.
      qc.invalidateQueries({ queryKey: accountKeys.all });
      /*
       * RP-9.1: a below-floor Propose deliberately does NOT transition — it creates an
       * Approval Request and holds the plan in Draft. Reporting "Plan is now Draft" would
       * read as a no-op, so the toast says what actually happened.
       */
      if (plan.pendingApprovalId != null) {
        toast.success("Sent for approval. The plan stays Open until Finance decides.");
        return;
      }
      toast.success(`Plan is now ${planStatusLabel(plan.status)}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to move the plan"),
  });
}

/**
 * The batched grid save. One request for every dirty cell.
 *
 * No toast on success: the grid shows "All changes saved" in its own status line, and a
 * toast per save would be constant noise while someone types across 27 months. Failure
 * still toasts, loudly, because unsaved hours are the thing worth interrupting for.
 */
export interface GridSaveInput {
  /** MonthByMonth. */
  cells?: Array<{ planLineId: string; month: string; hours: string | null }>;
  /** SteadyState — one hours-per-month figure per Plan Line (RP-4.4). */
  runRates?: Array<{ planLineId: string; hours: string | null }>;
}

export function useSaveGrid(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: GridSaveInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/grid`, "PUT", input),
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err.message ?? "Failed to save the grid"),
  });
}

export interface WorkstreamInput {
  name: string;
  type: string;
  description?: string | null;
  estimatedHours?: string | null;
  startMonth?: string | null;
  endMonth?: string | null;
}

export function useCreateWorkstream(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: WorkstreamInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/workstreams`, "POST", input),
    onSuccess: (_plan, input) => {
      invalidate();
      toast.success(`Added the "${input.name}" Workstream`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the Workstream"),
  });
}

/**
 * `estimatedHours` is on `WorkstreamInput` only for CREATE — once a Workstream exists its
 * estimate is a server-maintained rollup of its Estimated Hours Lines, not independently
 * patchable. See `UpdatePlanInput` above for the same reasoning at the plan level.
 */
export type UpdateWorkstreamInput = Partial<Omit<WorkstreamInput, "estimatedHours">>;

export function useUpdateWorkstream() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & UpdateWorkstreamInput) =>
      send<ResourcePlanDetailView>(`/api/workstreams/${id}`, "PATCH", patch),
    onSuccess: () => {
      invalidate();
      toast.success("Workstream updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Workstream"),
  });
}

export function useDeleteWorkstream() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (id: string) => send<ResourcePlanDetailView>(`/api/workstreams/${id}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Workstream removed, along with its Plan Lines");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the Workstream"),
  });
}

export interface PlanLineInput {
  workstreamId?: string | null;
  laborCategoryId: string;
  clientLaborCategory?: string | null;
  position?: string | null;
  billRate?: string;
  rateOverrideReason?: string | null;
  /** RP-4.4 — set straight away on a SteadyState plan. */
  steadyStateHours?: string | null;
}

export function useCreatePlanLine(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: PlanLineInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/lines`, "POST", input),
    onSuccess: () => {
      invalidate();
      toast.success("Plan Line added");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the Plan Line"),
  });
}

export function useUpdatePlanLine() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: { id: string } & Partial<PlanLineInput> & { resetRateToCard?: boolean }) =>
      send<ResourcePlanDetailView>(`/api/plan-lines/${id}`, "PATCH", patch),
    onSuccess: () => {
      invalidate();
      toast.success("Plan Line updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Plan Line"),
  });
}

export function useDeletePlanLine() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (id: string) => send<ResourcePlanDetailView>(`/api/plan-lines/${id}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Plan Line removed");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the Plan Line"),
  });
}

/** The more recently updated of two plan snapshots; on a tie, `b`. */
function newerPlan<T extends { updatedAt: string }>(
  a: T | undefined,
  b: T | undefined,
): T | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Date.parse(a.updatedAt) > Date.parse(b.updatedAt) ? a : b;
}

/**
 * RP-24 — replace a Plan Line's whole Share set (RM / ADMIN). `[]` un-splits it; one Share
 * collapses back to that Resource as the line's Confirmed Suggested Resource. `message` is
 * the success toast, so a grid save across several lines can say one thing once.
 */
export function useSetPlanLineShares() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({
      lineId,
      shares,
    }: {
      lineId: string;
      shares: PlanLineShareInput[];
      message?: string;
    }) => send<ResourcePlanDetailView>(`/api/plan-lines/${lineId}/shares`, "PUT", { shares }),
    onSuccess: (_plan, input) => {
      invalidate();
      if (input.message !== undefined) toast.success(input.message);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to save the split"),
  });
}

/**
 * RP-18.2. Confirm records who the plan PROPOSES — it does not put anyone on the Pod.
 * The toast says so, because the distinction is the whole point.
 */
export function useSetSuggestedResource(planId: string) {
  const invalidate = useInvalidatePlans();
  const qc = useQueryClient();
  // The last plan this hook got back. A second pick can land before the refetch that
  // follows the first, and the cache would then still show the plan above its floor.
  const lastAnswer = useRef<MarginWarningPlan & { updatedAt: string }>(undefined);
  return useMutation({
    mutationFn: ({
      lineId,
      resourceId,
      confirmed,
      reason,
      reasonNote,
    }: {
      lineId: string;
      resourceId: string | null;
      confirmed?: boolean;
      /** RP-32.1. Omit to keep the current reason (same Resource); null clears it. */
      reason?: SuggestedResourceReason | null;
      reasonNote?: string | null;
    }) =>
      send<ResourcePlanDetailView>(`/api/plan-lines/${lineId}/suggested-resource`, "PUT", {
        resourceId,
        confirmed,
        reason,
        reasonNote,
      }),
    // RP-7.6: the plan as it stood, so a pick that takes it below floor can say so —
    // whichever of the cache and this hook's last answer is newer.
    onMutate: () => ({
      before: newerPlan(
        qc.getQueryData<ResourcePlanDetailView>(resourcePlanKeys.detail(planId)),
        lastAnswer.current,
      ),
    }),
    onSuccess: (plan, input, context) => {
      lastAnswer.current = plan;
      invalidate();
      if (input.reason !== undefined && input.resourceId !== null) {
        toast.success(input.reason === null ? "Reason cleared" : "Reason saved");
      } else if (input.resourceId === null) {
        toast.success("Suggestion cleared");
      } else if (input.confirmed) {
        toast.success("Confirmed — they go onto the Pod when the plan is set to Staffed");
      } else {
        // Assigning someone lands the line on Proposed — say so, since it is the default.
        toast.success("Marked Proposed");
      }
      // A warning, never a block: the change has already saved (RP-18.7).
      const warning = marginDropWarning(context?.before, plan);
      if (warning !== null) toast.warning(warning);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to set the Suggested Resource"),
  });
}

/**
 * The Resource Manager's staffing status — separate from the plan's sales status
 * (`useTransitionResourcePlan`). `null` clears it back to "Not set".
 */
export function useSetPlanResourcingStatus(planId: string) {
  const invalidate = useInvalidatePlans();
  const qc = useQueryClient();
  // Staffed writes Assignments onto the Solution — its Pod & Fit tab, Fit Score, the
  // dashboard and every Resource's load move with it. Also on error: a commit that fails
  // part-way has still placed whoever came before the failure.
  const invalidateSolutionStaffing = () => {
    qc.invalidateQueries({ queryKey: solutionDetailKeys.all });
    qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
    qc.invalidateQueries({ queryKey: dashboardHeatmapKeys.all });
    qc.invalidateQueries({ queryKey: resourceDirectoryKeys.all });
  };
  return useMutation({
    mutationFn: (resourcingStatus: PlanResourcingStatus | null) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/resourcing-status`, "PUT", {
        resourcingStatus,
      }),
    onSuccess: (plan) => {
      invalidate();
      if (plan.resourcingStatus === "Staffed") {
        invalidateSolutionStaffing();
        toast.success("Plan Staffed — its Resources are now on the Solution");
      } else {
        toast.success("Resourcing status updated");
      }
    },
    onError: (err: Error) => {
      invalidate();
      invalidateSolutionStaffing();
      toast.error(err.message ?? "Failed to update the resourcing status");
    },
  });
}

/**
 * Submit to Resourcing: whoever can edit the plan moves its Resourcing status from Not set,
 * or Rejected, to In Review. Refused without a NOS Deal — the dialog says so before asking.
 */
export function useSubmitPlanToResourcing(planId: string) {
  const invalidate = useInvalidatePlans();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/submit-to-resourcing`, "POST"),
    onSuccess: () => {
      invalidate();
      // A resubmitted plan leaves the Rejected Plans report.
      qc.invalidateQueries({ queryKey: planRejectionKeys.all });
      toast.success("Submitted to Resourcing");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to submit the plan to Resourcing"),
  });
}

/**
 * An RM or Admin rejects a plan that is In Review or Staffing, with a required reason. The
 * server raises it as a Blocking Staffing Concern (RP-34), so the plan's Concerns and the
 * Rejected Plans report move too.
 */
export function useRejectResourcePlan(planId: string) {
  const invalidate = useInvalidatePlans();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/reject`, "POST", { reason }),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: planRejectionKeys.all });
      toast.success("Plan rejected — the author has been notified");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to reject the plan"),
  });
}

/**
 * What setting the plan to Staffed would write onto its Solution — who, in which
 * Position, with how many points — or why it is refused. Fetched only while the
 * confirmation dialog is open.
 */
export function usePlanStaffingPreview(planId: string, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.staffingPreview(planId),
    queryFn: async () =>
      readJsonOrThrow<PlanStaffing>(await fetch(`/api/plans/${planId}/staffing-preview`)),
    enabled,
  });
}

/**
 * Owner and Author are the same field (`ownerUserId` / `ownerName`) — this reassigns who
 * the plan belongs to, on a plan in any status, not only Draft.
 */
export function useSetPlanOwner(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (ownerUserId: string) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/owner`, "PUT", { ownerUserId }),
    onSuccess: (plan) => {
      invalidate();
      toast.success(`Author set to ${plan.ownerName ?? "—"}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the plan owner"),
  });
}

export function useSetSolutionFixedStaffed() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ solutionId, fixedStaffed }: { solutionId: string; fixedStaffed: boolean }) =>
      send<{ ok: true; fixedStaffed: boolean }>(
        `/api/solutions/${solutionId}/fixed-staffed`,
        "PUT",
        { fixedStaffed },
      ),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        result.fixedStaffed
          ? "Marked Fixed-Staffed — new plans will price on the Fixed rate column"
          : "Fixed-Staffed cleared — new plans price by Engagement Standard Tier",
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Solution"),
  });
}
